import { Badge, Box, Button, Group, Paper, Skeleton, Stack, Switch, Text, Tooltip, VisuallyHidden, type MantineColor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconChecks } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { api } from '../../api/client';
import { errorText, orgsByIdQuery, useInvalidate } from '../../api/hooks';
import { EmptyState, PageHeader, ToolbarActions } from '../../components/page';
import { useServerPages } from '../../components/pager';
import { when } from '../../lib/format';
import { isStaff } from '../../lib/roles';
import { Route as AppRoute, useOrgId, useTopLevel } from '../_app';
import { ICON_INLINE } from '../../theme';

export const Route = createFileRoute('/_app/notifications')({ component: Notifications });

const severityColor = (s: string): MantineColor => (s === 'critical' ? 'danger' : s === 'warning' ? 'warning' : 'instrument');

const HAIRLINE = '1px solid var(--mantine-color-default-border)';
// An identifier inside a caption line keeps the caption's size.
const INLINE_ID = { fontSize: 'inherit' } as const;

// The inbox: things the console noticed that a person should act on. Org
// admins see their organization's; staff see every organization's plus
// staff-only entries. Repeats within a day fold into one entry with a count.
export function Notifications() {
  const orgId = useOrgId();
  const { me } = AppRoute.useRouteContext();
  const staff = isStaff(me);
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Among MotionSafe's own pages the inbox is every organization's; inside an organization it is that one's.
  const top = useTopLevel();
  const scope = top ? '' : orgId;
  // A page at a time, newest first; a changed filter starts again from the first page.
  const pages = useServerPages({ key: ['notifications'], narrowed: `${scope}|${unreadOnly}`, read: (page) => api.notification.listNotifications({ orgId: scope, unreadOnly, ...page }), noun: 'notifications', refetchInterval: 60_000 });
  const list = pages.query;
  // Staff read entries from many organizations; name each one.
  const orgs = useQuery({ ...orgsByIdQuery(staff ? (list.data?.notifications ?? []).map((n) => n.orgId) : []), placeholderData: keepPreviousData }).data;
  const inv = useInvalidate();
  const fail = (e: unknown) => notifications.show({ color: 'danger', message: errorText(e) });
  const markAll = useMutation({ mutationFn: () => api.notification.markNotificationsRead({ ids: [], orgId: scope }), onSuccess: (r) => { void inv('notifications'); notifications.show({ message: r.marked ? `${r.marked} marked read.` : 'Nothing unread.' }); }, onError: fail });
  const markOne = useMutation({ mutationFn: (id: string) => api.notification.markNotificationsRead({ ids: [id] }), onSuccess: () => inv('notifications'), onError: fail });
  const rows = list.data?.notifications ?? [];
  const unread = list.data?.unread ?? 0;

  return (
    <>
      <PageHeader
        title={top ? 'All notifications' : 'Notifications'}
        description="Things the console noticed that need a person: a seat refused at its limit, an operator trying a device that belongs to another organization. Each is also in the audit trail."
      />

      <Group gap="lg" align="center" wrap="wrap" mb="md">
        <Switch label="Unread only" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.currentTarget.checked)} />
        <ToolbarActions>
          {list.isSuccess && <Text size="sm" c="dimmed" className="num">{unread} unread</Text>}
          <Button size="sm" variant="default" leftSection={<IconChecks {...ICON_INLINE} />} onClick={() => markAll.mutate()} disabled={!unread || markAll.isPending}>Mark all read</Button>
        </ToolbarActions>
      </Group>

      <Paper p={0}>
        {list.isPending && [0, 1, 2, 3].map((i) => (
          <Group key={i} wrap="nowrap" align="flex-start" gap="md" px="lg" py="md" aria-hidden="true" {...(i > 0 ? { style: { borderTop: HAIRLINE } } : {})}>
            <Skeleton height={20} width={72} />
            <Stack gap={8} flex={1}>
              <Skeleton height={14} width="40%" />
              <Skeleton height={12} width="75%" />
              <Skeleton height={12} width="60%" />
            </Stack>
            <Skeleton height={12} width={56} />
          </Group>
        ))}

        {list.isError && <Text size="sm" c="danger" ta="center" p="xl">{errorText(list.error)}</Text>}

        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            icon={IconBell}
            title={unreadOnly ? 'Nothing unread' : 'Nothing reported yet'}
            body="A seat refused at its limit and an operator trying a device that belongs to another organization are reported here as they happen. Each is also in the audit trail."
          />
        )}

        {rows.map((n, i) => {
          const isUnread = !n.readAt;
          const org = staff && n.orgId ? orgs?.find((o) => o.id === n.orgId) : undefined;
          const marking = markOne.isPending && markOne.variables === n.id;
          return (
            // Three columns on a desktop; on a phone the message takes the
            // full width under its severity, and the time and action follow it.
            <Group
              key={n.id}
              component="article"
              wrap="wrap"
              align="flex-start"
              gap="md"
              px="lg"
              py="md"
              {...(i > 0 ? { style: { borderTop: HAIRLINE } } : {})}
            >
              <Box w={84} flex="none" pt={1}>
                <Badge color={severityColor(n.severity)}>{n.severity}</Badge>
              </Box>
              <Stack gap={2} flex={{ base: '1 1 100%', xs: '1 1 0' }} miw={0}>
                <Text size="sm" fw={isUnread ? 600 : 400}>
                  {isUnread && <VisuallyHidden>Unread: </VisuallyHidden>}
                  {n.title}
                </Text>
                <Text size="sm" {...(isUnread ? {} : { c: 'dimmed' })}>{n.body}</Text>
                {(n.count > 1 || (staff && n.orgId)) && (
                  <Text size="xs" c="dimmed" mt={2}>
                    {n.count > 1 && <span className="num">{n.count} times</span>}
                    {n.count > 1 && staff && n.orgId && ' · '}
                    {staff && n.orgId && (org
                      ? <>{org.name} <span className="id" style={INLINE_ID}>{org.slug}</span></>
                      : <span className="id" style={INLINE_ID}>{n.orgId}</span>)}
                  </Text>
                )}
              </Stack>
              <Stack gap={6} align="flex-end" flex="none" ml="auto">
                <Tooltip label={when(n.lastAt)}>
                  <Text size="xs" c="dimmed" className="num" style={{ whiteSpace: 'nowrap' }}>{when(n.lastAt, { relative: true })}</Text>
                </Tooltip>
                {isUnread && (
                  <Button variant="subtle" size="compact-sm" onClick={() => markOne.mutate(n.id)} disabled={marking} aria-label={`Mark read: ${n.title}`}>Mark read</Button>
                )}
              </Stack>
            </Group>
          );
        })}
        {pages.footer}
      </Paper>
    </>
  );
}
